import React, { FC } from 'react';
import styled from 'styled-components';
import { ReactComponent as NotificationIcon } from '../../layout/icons/red-circle-exclamation-small.svg';
import { PrimaryButton } from '../../common-components/Buttons';
import { Typography } from '../../layout/components/Typography';
import { ColorScheme } from '../../layout/colorScheme';
import CMSStore from '../../stores/content-stores/CMSStore';
import type { BalanceResponse } from '../../swagger/api';
import useIsMobileView from '../../hooks/useIsMobileView';
import decimalFormatting from '../../utils/decimalFormatting';

const CustomNotificationBar = styled.div<{
  type?: 'positive' | 'negative' | 'zero';
  $isMobile?: boolean;
}>`
  background: ${({ type }) => {
    if (type === 'positive') return 'rgba(250, 226, 226, 0.5)';
    if (type === 'negative') return 'rgba(222, 233, 228, 0.7)';
    if (type === 'zero') return ColorScheme.Yellow;
    return 'rgba(240, 240, 240, 0.8)';
  }};
  border-radius: 8px;
  padding: 10px 22px;
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  ${({ $isMobile }) =>
    $isMobile &&
    `
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    padding: 16px 20px 20px 16px;
  `}
`;

const NotificationContent = styled.div<{ $isMobile?: boolean; $marginBottom?: boolean }>`
  display: flex;
  margin-top: 2px;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  ${({ $isMobile }) =>
    $isMobile &&
    `
    justify-content: flex-start;
  `}
  ${({ $marginBottom }) => $marginBottom && `margin-bottom: 10px;`}
`;

const StyledNotificationIcon = styled(NotificationIcon)<{
  balanceType?: 'positive' | 'negative' | 'zero';
}>`
  flex-shrink: 0;
  margin-top: -2px;
  align-self: start;
  & path {
    stroke: ${({ balanceType }) =>
      balanceType === 'positive'
        ? ColorScheme.ErrorRed
        : balanceType === 'negative'
        ? ColorScheme.BrobizzGreen
        : '#2D2D2D'};
  }
`;

export type BalanceSettlementBannerVariant = 'private' | 'business';

interface BalanceSettlementBannerProps {
  balance: BalanceResponse;
  variant: BalanceSettlementBannerVariant;
  cmsStore: CMSStore;
  onSettlementClick: () => void;
}

const getCmsKeySuffix = (variant: BalanceSettlementBannerVariant) =>
  variant === 'business' ? 'Business' : '';

const BalanceSettlementBanner: FC<BalanceSettlementBannerProps> = ({
  balance,
  variant,
  cmsStore,
  onSettlementClick
}) => {
  const isMobileView = useIsMobileView();
  const balanceType = balance.balanceType?.toLowerCase() as
    | 'positive'
    | 'negative'
    | 'zero'
    | undefined;
  const amount = decimalFormatting(Math.abs(balance.totalBalance ?? 0));
  const currency = balance.currency?.toUpperCase() || 'DKK';
  const suffix = getCmsKeySuffix(variant);

  const getBannerText = () => {
    if (balanceType === 'positive') {
      return cmsStore.getContent(`SettlementBannerPositive${suffix}`)?.content;
    }
    if (balanceType === 'negative') {
      return cmsStore.getContent(`SettlementBannerNegative${suffix}`)?.content;
    }
    return cmsStore.getContent(`SettlementBannerZero${suffix}`)?.content;
  };

  const getButtonText = () => {
    if (balanceType === 'positive') {
      return cmsStore.getContent(`PayNowButton${suffix}`)?.content;
    }
    if (balanceType === 'negative') {
      return cmsStore.getContent(`WithdrawButton${suffix}`)?.content;
    }
    return cmsStore.getContent(`SettleButton${suffix}`)?.content;
  };

  const bannerText = (
    <Typography
      variant="contentSmall"
      as="span"
      align="left"
      style={{ flex: isMobileView ? undefined : 1, color: '#2d2d2d' }}
    >
      {getBannerText()}
    </Typography>
  );

  const amountTypography = balanceType !== 'zero' && (
    <Typography
      variant="contentSmallLink"
      as="p"
      align={isMobileView ? 'center' : 'left'}
      color={
        balanceType === 'positive'
          ? ColorScheme.ErrorRed
          : balanceType === 'negative'
          ? ColorScheme.BrobizzGreen
          : undefined
      }
      style={{
        marginBottom: '0px',
        ...(isMobileView && {
          marginTop: '24px',
          marginBottom: '10px',
          alignSelf: 'center'
        })
      }}
    >
      {amount} {currency}
    </Typography>
  );

  const settlementButton = (
    <PrimaryButton
      onClick={onSettlementClick}
      style={{
        ...(isMobileView && { alignSelf: 'center' })
      }}
    >
      {getButtonText()}
    </PrimaryButton>
  );

  return (
    <CustomNotificationBar type={balanceType} $isMobile={isMobileView}>
      {isMobileView ? (
        <>
          <NotificationContent $isMobile $marginBottom={balanceType === 'zero'}>
            <StyledNotificationIcon balanceType={balanceType} />
            {bannerText}
          </NotificationContent>
          {amountTypography}
          {settlementButton}
        </>
      ) : (
        <>
          <NotificationContent>
            <StyledNotificationIcon balanceType={balanceType} />
            {bannerText}
            {amountTypography}
          </NotificationContent>
          {settlementButton}
        </>
      )}
    </CustomNotificationBar>
  );
};

export default BalanceSettlementBanner;
